import { Flex, Table, Tabs, TableColumnType } from 'antd';
import './index.css';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { TableList } from '@/types/common';
import {
  FilterValue,
  TableCurrentDataSource,
  TablePaginationConfig,
  SorterResult,
} from 'antd/es/table/interface';

export interface TabsTableRef {
  getSelectedKeys: () => string[];
  resetSelectedKeys: () => void;
  refresh: () => void;
}

interface TabsTableProps {
  defaultTabKey?: string;
  tabItems?: {
    key: string;
    label: string;
  }[];
  rightContent?: React.ReactNode;
  columns: unknown[];
  queryKey: string;
  queryFn: (
    page: number,
    pageSize: number,
    tabKey?: string
  ) => Promise<TableList<unknown>>;
  pageSize?: number;
  rowKey?: string;
  extraKeys?: unknown[];
  allowRowSelection?: boolean;
  onSelectionChange?: (selectedKeys: string[]) => void;
  onTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<unknown> | SorterResult<unknown>[],
    extra: TableCurrentDataSource<unknown>
  ) => void;
}

const TabsTable = forwardRef<TabsTableRef, TabsTableProps>(
  (
    {
      defaultTabKey,
      tabItems,
      rightContent,
      columns,
      queryKey,
      extraKeys,
      queryFn,
      pageSize = 20,
      rowKey = 'id',
      allowRowSelection = false,
      onSelectionChange,
      onTableChange,
    },
    ref
  ) => {
    const isMobile = useAtomValue(isMobileAtom);

    const [tabKey, setTabKey] = useState<string>(defaultTabKey || '');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const tablePageSize = isMobile ? 10 : pageSize;

    const handleTabChange = (key: string) => {
      if (!tabItems) return;
      setTabKey(key);
      setCurrentPage(1);
    };

    const handleRowSelectionChange = (selectedRowKeys: string[]) => {
      if (!allowRowSelection) return;
      setSelectedKeys(selectedRowKeys);
      onSelectionChange?.(selectedRowKeys);
    };

    const isShowRightContent = useMemo(() => {
      return !isMobile && rightContent;
    }, [isMobile, rightContent]);

    const {
      data: tableData,
      isLoading,
      refetch,
    } = useQuery({
      queryKey: [queryKey, tabKey, currentPage, ...(extraKeys || [])],
      queryFn: () => queryFn(currentPage, tablePageSize, tabKey),
      refetchOnWindowFocus: false,
    });

    const handleRefresh = () => {
      setCurrentPage(1);
      void refetch();
    };

    useImperativeHandle(ref, () => ({
      getSelectedKeys: () => selectedKeys,
      resetSelectedKeys: () => setSelectedKeys([]),
      refresh: handleRefresh,
    }));

    return (
      <div className="tabs-table-container">
        <Flex justify="space-between" align="center">
          {tabItems && (
            <Tabs
              type="card"
              size="small"
              items={tabItems}
              activeKey={tabKey}
              onChange={handleTabChange}
              className="tabs-container"
            />
          )}
          {isShowRightContent && <div>{rightContent}</div>}
        </Flex>

        <Table
          size="small"
          loading={isLoading}
          columns={columns as TableColumnType<unknown>[]}
          dataSource={tableData?.records || []}
          rowKey={rowKey}
          scroll={{ x: 'max-content' }}
          onChange={onTableChange}
          pagination={{
            size: isMobile ? 'small' : 'default',
            pageSize: tablePageSize,
            showSizeChanger: false,
            total: tableData?.total,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
          }}
          rowSelection={
            allowRowSelection
              ? {
                  selectedRowKeys: selectedKeys,
                  type: 'checkbox',
                  fixed: true,
                  onChange: (selectedRowKeys) => {
                    handleRowSelectionChange(selectedRowKeys as string[]);
                  },
                }
              : undefined
          }
        />
      </div>
    );
  }
);

TabsTable.displayName = 'TabsTable';

export default TabsTable;
